import {
    Button,
    Flex,
    Grid,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import type { MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';
import { useDesignBoardCollaboratorsContext } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import {
    ButtonBase,
    CancelButton,
    ConfirmationContainer,
    ConfirmationContentArea,
    FooterContainer,
    FooterDefault,
    Message,
    Title,
} from '@modules/design-board/component/manage/collaborator-overview/components/Confirmation.style';

interface ConfirmationProps {
    onConfirm?: (e: MouseEvent) => void;
    onCancel?: (e: MouseEvent) => void;
    title?: string;
    content?: string;
    confirmText?: string;
    cancelText?: string;
    render?: {
        title?: ReactNode;
        message?: ReactNode;
        footer?: ReactNode;
        confirmTrigger?: ReactNode;
        cancelTrigger?: ReactNode;
    };
}

export const Confirmation = ({
    render,
    title,
    content,
    onConfirm,
    onCancel,
    cancelText,
    confirmText,
}: ConfirmationProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const context = useDesignBoardCollaboratorsContext();
    const { desktop } = useAppDisplayModeContext();

    const buttonBorder = `1px solid ${theme.siteVariables.colors.charcoal[250]}`;

    const handleBackgroundClick = (e: MouseEvent<HTMLElement>) => {
        if (!(e.target === e.currentTarget)) {
            return;
        }
        e.stopPropagation();
        context.dispatch({ type: 'hide-remove-collaborator-confirmation' });
    };

    const handleConfirmation = () => {
        context.dispatch({ type: 'hide-remove-collaborator-confirmation' });
    };

    const handleCancel = () => {
        context.dispatch({ type: 'hide-remove-collaborator-confirmation' });
    };

    if (context.state.removeCollaboratorConfirmationVisibility === 'hidden') {
        return null;
    }

    return (
        <Flex
            className={css(ConfirmationContainer)}
            column={true}
            onClick={handleBackgroundClick}
        >
            <Flex column={true} className={css(ConfirmationContentArea)}>
                {render?.title ?? (
                    <span className={css(Title)}>
                        {title ?? 'Confirmation Required'}
                    </span>
                )}
                {render?.message ?? (
                    <p className={css(Message)}>
                        {content ?? 'Are you sure you want to do this?'}
                    </p>
                )}
                <div className={css(FooterContainer)}>
                    {render?.footer ? (
                        render.footer
                    ) : (
                        <Grid
                            columns={`${pxToRem(desktop ? 143 : 126)} ${pxToRem(
                                desktop ? 143 : 126
                            )}`}
                            className={css(FooterDefault)}
                        >
                            {render?.cancelTrigger ? (
                                <div onClick={handleCancel}>
                                    {render.cancelTrigger}
                                </div>
                            ) : (
                                <div
                                    onClick={(e) => {
                                        handleCancel();
                                        onCancel && onCancel(e);
                                    }}
                                >
                                    <Button
                                        className={classNames(
                                            css(ButtonBase, CancelButton)
                                        )}
                                        variant="outlined"
                                        color="dark"
                                        size={desktop ? 16 : 14}
                                        variables={{
                                            darkOutlinedButtonBorderNormal:
                                                buttonBorder,
                                        }}
                                    >
                                        {cancelText ?? 'CANCEL'}
                                    </Button>
                                </div>
                            )}

                            {render?.confirmTrigger ? (
                                <div
                                    onClick={(e) => {
                                        handleConfirmation();
                                        onConfirm && onConfirm(e);
                                    }}
                                >
                                    {render.confirmTrigger}
                                </div>
                            ) : (
                                <div
                                    onClick={(e) => {
                                        onConfirm && onConfirm(e);
                                    }}
                                >
                                    <Button
                                        className={css(ButtonBase)}
                                        color="dark"
                                        type={'submit'}
                                        size={desktop ? 16 : 14}
                                        variables={{
                                            darkButtonBorderNormal:
                                                buttonBorder,
                                            darkButtonBorderHover: buttonBorder,
                                            darkButtonBorderSelected:
                                                buttonBorder,
                                        }}
                                    >
                                        {confirmText ?? 'CONFIRM'}
                                    </Button>
                                </div>
                            )}
                        </Grid>
                    )}
                </div>
            </Flex>
        </Flex>
    );
};
